import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EconomicOperator, EconomicOperatorCreate, EconomicOperatorEdit, EconomicOperatorView } from '../../shared/models/economic-operator';
import { Observable, of } from 'rxjs';
import { ApiParamConstants, ApiUrlConstants } from '../../shared/utils/api-constants';
import { ListHttpParameters } from '../../shared/models/list-http-parameters';
import { EOCreationSuccessResponse } from '../../shared/models/eocreation-success-response';
import { ListView } from '../../shared/models/list-view';
import { UrlConstants } from '../../shared/utils/url-constants';
import { StatusUpdateRequest } from '../../shared/models/status-update-request';
import { EoApprovalRequest } from '../../shared/models/eo-approval-request';
import { AppConfigService } from './app-config.service';
import { AppHttpParams } from '../../shared/models/app-http-params';
import { KeyWordTranslatorService } from './key-word-translator.service';
import { EntityStatusEnum } from '../../shared/enums/entity-status.enum';
import { EOTypeEnum } from '../../shared/enums/eo-type-enum';
import { TranslateService } from '@ngx-translate/core';
import { EoApproval } from '../../shared/models/eo-approval';
import { EoApprovalView } from '../../shared/models/eo-approval-view';
import { AppConstants } from '../../shared/utils/app-constants';
import { PermissionDetails } from 'src/app/shared/models/permission-details';
import { EoWithUidCount } from 'src/app/shared/models/eoWithUidCount';
import { map, tap, catchError } from 'rxjs/operators';
import { OperatorWithProductionVolume } from 'src/app/shared/models/operator-with-production-volume';

@Injectable({
  providedIn: 'root'
})
export class EconomicOperatorsService {

  constructor(private http: HttpClient,
    private keyWordTranslatorService: KeyWordTranslatorService,
    private translateService: TranslateService) {
  }

  create(economicOperator: EconomicOperatorCreate): Observable<EOCreationSuccessResponse> {
    this.updateModel(economicOperator as EconomicOperator);
    const appParams = new AppHttpParams({ responseLabel: 'economicOperators.newEconomicOperator', notifySuccess: true });
    return this.http.post<EOCreationSuccessResponse>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS, economicOperator, { params: appParams });
  }

  createExternal(economicOperator: EconomicOperatorCreate): Observable<EOCreationSuccessResponse> {
    this.updateModel(economicOperator as EconomicOperator);
    const appParams = new AppHttpParams({ responseLabel: 'createAccount' });
    return this.http.post<EOCreationSuccessResponse>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS_EXTERNAL, economicOperator, { params: appParams });
  }

  update(eoId: string, economicOperator: EconomicOperatorEdit): Observable<EOCreationSuccessResponse> {
    this.updateModel(economicOperator as EconomicOperator);
    const appParams = new AppHttpParams({ responseLabel: 'economicOperators.editEconomicOperator', notifySuccess: true });
    return this.http.patch<EOCreationSuccessResponse>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS + UrlConstants.SEPARATOR + eoId, economicOperator, { params: appParams });
  }

  getListView(params: ListHttpParameters): Observable<ListView<EconomicOperatorView>> {
    const originalStatus = params.status;
    if (params.objectName && !params.objectId) {
      return of(null);
    }
    if (params.keyword) {
      // Preserve strict hardcoded filters before translation runs
      const originalCanRequestUid = params.canRequestUid;
      const originalBlocked = params.blocked;

      params = this.keyWordTranslatorService.translateCountries(params, ApiParamConstants.LIST_COUNTRIES);
      params = this.keyWordTranslatorService.translateEnumValues(params, ApiParamConstants.LIST_STATUS, EntityStatusEnum);
      params = this.keyWordTranslatorService.translateEnumValues(params, ApiParamConstants.LIST_TYPE, EOTypeEnum);
      params = this.keyWordTranslatorService.translateBoolean(params, ApiParamConstants.LIST_EO_CAN_ORDER_UID,
        this.translateService.instant('listParams.EO_CAN_ORDER_UID.canOrder'), this.translateService.instant('listParams.EO_CAN_ORDER_UID.cantOrder'));
      if (AppConfigService.settings.economicOperator.reliabilityEnabled) {
        params = this.keyWordTranslatorService.translateBoolean(params, ApiParamConstants.LIST_EO_RELIABLE,
          this.translateService.instant('listParams.EO_RELIABLE.reliable'), this.translateService.instant('listParams.EO_RELIABLE.notReliable'));
      }
      params = this.keyWordTranslatorService.translateString(params, ApiParamConstants.LIST_EO_REG_EOID, this.translateService.instant('listParams.REG_EOID'));
      params.status = originalStatus;

      // Restore strict hardcoded filters so the keyword parser doesn't overwrite them to null
      if (originalCanRequestUid !== null && originalCanRequestUid !== undefined) {
        params.canRequestUid = originalCanRequestUid;
      }
      if (originalBlocked !== null && originalBlocked !== undefined) {
        params.blocked = originalBlocked;
      }
    }
    const userRole = JSON.parse(localStorage.getItem(AppConstants.CURRENT_USER_ROLE));
    
    const httpParams = params.getHttpParams();
    if (!httpParams[ApiParamConstants.LIST_SORT]) {
      httpParams[ApiParamConstants.LIST_SORT] = 'UPDATEDATE_DESC';
    }

    const appParams = new AppHttpParams({ params: httpParams, notifyError: true });
    return this.http.get<ListView<EconomicOperatorView>>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS, { params: appParams, headers: { 'X-User-Role': userRole } });
  }

  getOne(eoId: string): Observable<EconomicOperatorView> {
    const appParams = new AppHttpParams({ responseLabel: 'economicOperators.viewEconomicOperator', notifyError: true });
    return this.http.get<EconomicOperatorView>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS + UrlConstants.SEPARATOR + eoId, { params: appParams });
  }

  updateStatus(eoId: string, eoStatusUpdateRequest: StatusUpdateRequest): Observable<EOCreationSuccessResponse> {
    const appParams = new AppHttpParams({ responseLabel: 'economicOperators.updateStatus', notifyError: true, notifySuccess: true });
    return this.http.patch<EOCreationSuccessResponse>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS
      + UrlConstants.SEPARATOR + eoId + ApiUrlConstants.ECONOMIC_OPERATORS_STATUS, eoStatusUpdateRequest, { params: appParams });
  }

  approve(eoId: string, eoApprovalRequest: EoApprovalRequest): Observable<string> {
    const appParams = new AppHttpParams({ responseLabel: 'economicOperators.updateStatus', notifySuccess: true });
    return this.http.patch<string>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS + UrlConstants.SEPARATOR + eoId + ApiUrlConstants.ECONOMIC_OPERATORS_APPROVE,
      eoApprovalRequest, { params: appParams });
  }

  validateExternalEconomicOperator(token: string) {
    let params = new HttpParams();
    params = params.set(ApiParamConstants.VALIDATE_EXTERNAL_EO_TOKEN, token);

    return this.http.get<string>(AppConfigService.apiUrl + ApiUrlConstants.ECONOMIC_OPERATORS_EXTERNAL_VALIDATE, { params });
  }

  getEOsHavingPendingApprovals() {
    const appParams = new AppHttpParams({ responseLabel: 'economicOperators.approvals', notifyError: true });

    return this.http.get<ListView<EoApproval>>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS + ApiUrlConstants.ECONOMIC_OPERATOR_PENDING_APPROVALS, { params: appParams });
  }

  getEOWithPendingApprovals(eoid: string) {
    const appParams = new AppHttpParams({ responseLabel: 'economicOperators.approvals', notifyError: true });

    return this.http.get<EoApprovalView>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS + ApiUrlConstants.ECONOMIC_OPERATOR_PENDING_APPROVALS + ApiUrlConstants.SEPARATOR + eoid,
      { params: appParams });
  }

  getAccountNames(eoid: string): Observable<ListView<PermissionDetails>> {

    return this.http.get<ListView<PermissionDetails>>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS + ApiUrlConstants.EO_PERMISSION + ApiUrlConstants.SEPARATOR + eoid,
    );
  }

  addPermission(eoid: string, keycloakId: string, index: number): Observable<void> {

    const appParams = new HttpParams()
      .set('keycloakId', keycloakId)
      .set('index', index.toString());
    return this.http.post<void>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS + ApiUrlConstants.EO_PERMISSION + ApiUrlConstants.SEPARATOR + eoid, null,
      { params: appParams });
  }

  deletePermission(eoid: string, keycloakId: string, index: number): Observable<void> {
    const appParams = new AppHttpParams({
      responseLabel: 'economicOperators.approvals',
      notifyError: true
    }).set('keycloakId', keycloakId).set('index', index.toString());

    return this.http.delete<void>(AppConfigService.eoApiUrl + ApiUrlConstants.ECONOMIC_OPERATORS + ApiUrlConstants.EO_PERMISSION + ApiUrlConstants.SEPARATOR + eoid,
      { params: appParams });
  }

  updateModel(model: EconomicOperator) {
    model.VAT_R && (delete model.TAX_N);
    !model.VAT_R && (delete model.VAT_N);
    !model.EO_ExciseNumber1 && (delete model.EO_ExciseNumber2);
    !model.Reg_3RD && (delete model.Reg_EOID);
    model.Preferred_Language = this.translateService.currentLang;
    !model.EO_GLN && (delete model.EO_GLN);

    if (!model.Request_UID) {
      delete model.EO_Billing_Address;
      delete model.EO_Billing_Firstname;
      delete model.EO_Billing_Lastname;
      delete model.EO_Billing_Email;
      delete model.EO_Billing_Phone;
      delete model.EO_Billing_VAT_R;
      delete model.EO_Billing_TAX_N;
      delete model.EO_Billing_VAT_N;
      delete model.EO_Billing_Name;
    } else {
      if (model.EO_Billing_VAT_R) {
        delete model.EO_Billing_TAX_N;
      } else {
        delete model.EO_Billing_VAT_N;
      }
    }

  }

  /**
 * Gets economic operators with their UID production counts
 * @returns Observable of economic operators with production volumes
 */
  getEconomicOperatorsWithUidCounts(): Observable<OperatorWithProductionVolume[]> {
    const appParams = new AppHttpParams({ notifyError: true });
    return this.http.get<EoWithUidCount[]>(
      AppConfigService.eoApiUrl + ApiUrlConstants.DASHBOARD_METRICS + ApiUrlConstants.EO_UIDS,
      { params: appParams }
    ).pipe(
      map(response => {
        return response.map(item => ({
          name: item.eo.EO_Name1,
          companyId: item.eo.EO_ID,
          factory: 'Name', // Default value or customize based on your needs
          ytdProductionVolume: item.count || 0
        }));
      })
    );
  }

  getEOSuggestions(keyword: string): Observable<{ [key: string]: string[] }> {
    if (!keyword?.trim()) {
      return of({});
    }
  
    return this.http.get<{ [key: string]: string[] }>(
      `${AppConfigService.eoApiUrl}/economic-operators/suggestions`,
      { params: new HttpParams().set('keyword', keyword).set('limit', '5') }
    ).pipe(
      catchError(() => of({}))
    );
  }

  getDashboardList(params?: any): Observable<any[]> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined) {
          httpParams = httpParams.set(key, String(params[key]));
        }
      });
    }
    return this.http.get<any[]>(
      `${AppConfigService.eoApiUrl}${ApiUrlConstants.ECONOMIC_OPERATORS}/dashboard`,
      { params: httpParams }
    );
  }
  

}
